// src/api/inventory.js

import {
  DynamoDBClient,
  QueryCommand,
  GetItemCommand,
  PutItemCommand,
  UpdateItemCommand,
  DeleteItemCommand
} from '@aws-sdk/client-dynamodb';
import getAwsConfig from '../awsconfig';

const TABLE = 'inventory';
const PRODUCTS_TABLE = 'allSeeds';

async function makeClient() {
  const cfg = await getAwsConfig();
  return new DynamoDBClient(cfg);
}

function unmarshall(item) {
  return {
    itemType:         item.itemType.S,
    itemId:           item.itemId.S,
    name:             item.name?.S        || '',
    description:      item.description?.S || '',
    quantity:         item.quantity?.N    ? Number(item.quantity.N) : 0,
    unit:             item.unit?.S        || '',
    productRefId:     item.productRefId?.S|| '',
    imageUrls:        item.imageUrls?.L    ? item.imageUrls.L.map(x => x.S) : [],
    location:         item.location?.S    || '',
    reorderThreshold: item.reorderThreshold?.N
                        ? Number(item.reorderThreshold.N)
                        : 0,
    packagedCount:    item.packagedCount?.N
                        ? Number(item.packagedCount.N)
                        : 0,
    ready:            item.ready?.BOOL    || false,
    createdAt:        item.createdAt?.S    || '',
    updatedAt:        item.updatedAt?.S    || ''
  };
}

export async function listItems(itemType) {
  try {
    const db = await makeClient();
    const out = await db.send(new QueryCommand({
      TableName: TABLE,
      KeyConditionExpression: 'itemType = :t',
      ExpressionAttributeValues: { ':t': { S: itemType } }
    }));
    return (out.Items || []).map(unmarshall);
  } catch (error) {
    console.error(`Error listing items for type ${itemType}:`, error);
    throw new Error(`Failed to load ${itemType} items: ${error.message}`);
  }
}

export async function getItem(itemType, itemId) {
  try {
    const db = await makeClient();
    const out = await db.send(new GetItemCommand({
      TableName: TABLE,
      Key: {
        itemType: { S: itemType },
        itemId:   { S: itemId }
      }
    }));
    return out.Item ? unmarshall(out.Item) : null;
  } catch (error) {
    console.error(`Error getting item ${itemId} of type ${itemType}:`, error);
    throw new Error(`Failed to load item: ${error.message}`);
  }
}

export async function createItem(data) {
  try {
    // Validate required fields
    if (!data.itemType || !data.itemId) {
      throw new Error('itemType and itemId are required');
    }
    if (!data.name || data.name.trim() === '') {
      throw new Error('Item name is required');
    }

    const db  = await makeClient();
    const now = new Date().toISOString();

    // start with only the required PK & timestamps
    const item = {
      itemType:  { S: data.itemType },
      itemId:    { S: data.itemId },
      createdAt: { S: now },
      updatedAt: { S: now }
    };

  // helper to add if defined/non‑empty
  const addS = (key, val) => { if (val) item[key] = { S: val }; };
  const addN = (key, val) => {
    if (typeof val === 'number') {
      item[key] = { N: String(val) };
    }
  };
  const addBOOL = (key, val) => {
    if (typeof val === 'boolean') item[key] = { BOOL: val };
  };
  const addL = (key, arr) => {
    if (Array.isArray(arr) && arr.length) {
      // Ensure all items in the array are strings and non-empty
      const validItems = arr.filter(x => x && typeof x === 'string' && x.trim() !== '');
      if (validItems.length) {
        item[key] = { L: validItems.map(x => ({ S: x })) };
      }
    }
  };

  addS('name',             data.name);
  addS('description',      data.description);
  addN('quantity',         data.quantity);
  addS('unit',             data.unit);
  addS('location',         data.location);
  addN('reorderThreshold', data.reorderThreshold);
  addN('packagedCount',    data.packagedCount);
  addBOOL('ready',         data.ready);
  addL('imageUrls',        data.imageUrls);
  addS('productRefId',     data.productRefId);

    await db.send(new PutItemCommand({
      TableName: TABLE,
      Item: item
    }));
    
    // If this item is linked to a product (seed or clone), update it in the products table
    if (data.productRefId) {
      await syncWithProductListing(data);
    }
  } catch (error) {
    console.error('Error creating item:', error);
    throw new Error(`Failed to create item: ${error.message}`);
  }
}

// Function to sync inventory changes with product listing
async function syncWithProductListing(inventoryItem) {
  if (!inventoryItem.productRefId) return;
  
  try {
    const db = await makeClient();
    
    // First, find the product in allSeeds table
    // We need to scan since we don't know the createdDate (which is part of the key)
    const { Items } = await db.send(new QueryCommand({
      TableName: PRODUCTS_TABLE,
      KeyConditionExpression: 'id = :id',
      ExpressionAttributeValues: {
        ':id': { S: inventoryItem.productRefId }
      }
    }));
    
    if (!Items || Items.length === 0) {
      console.debug('No matching product found for', inventoryItem.productRefId);
      return;
    }
    
    const product = Items[0];
    const productId = product.id.S;
    const createdDate = product.createdDate.S;
    
    // Calculate total inventory value based on itemType
    let newInventoryValue = 0;
    if (inventoryItem.itemType === 'PRODUCT-SEED') {
      // For seeds, inventory is the unpacked seeds count
      newInventoryValue = inventoryItem.quantity;
    } else if (inventoryItem.itemType === 'PRODUCT-CLONE') {
      // For clones, inventory is the quantity
      newInventoryValue = inventoryItem.quantity;
    }
    
    // Only update if the inventory is different
    if (product.inventory?.N !== String(newInventoryValue)) {
      // Update the product in allSeeds table
      await db.send(new UpdateItemCommand({
        TableName: PRODUCTS_TABLE,
        Key: {
          id: { S: productId },
          createdDate: { S: createdDate }
        },
        UpdateExpression: 'SET inventory = :inv, updatedAt = :u',
        ExpressionAttributeValues: {
          ':inv': { N: String(newInventoryValue) },
          ':u': { S: new Date().toISOString() }
        }
      }));
      
      console.debug(`Updated product ${productId} inventory to ${newInventoryValue}`);
    }
  } catch (error) {
    console.error('Error syncing with product listing:', error);
    // Don't throw - we don't want to fail the inventory update if this sync fails
  }
}

export async function updateItem(itemType, itemId, updates) {
  const db  = await makeClient();
  const now = new Date().toISOString();

  // We'll build these in lock‑step
  const exprParts  = [];
  const attrNames  = {};
  const attrValues = { ':u': { S: now } };

  // Counter for valid fields
  let counter = 0;

  Object.entries(updates).forEach(([key, val]) => {
    // skip null/undefined
    if (val === undefined || val === null) return;
    
    // Skip updatedAt as we handle it separately
    if (key === 'updatedAt') return;

    // determine placeholder names
    const nameKey = `#f${counter}`;
    const valKey  = `:v${counter}`;
    let added    = false;

    // marshal the value
    if (typeof val === 'number') {
      attrValues[valKey] = { N: val.toString() };
      added = true;
    } else if (typeof val === 'boolean') {
      attrValues[valKey] = { BOOL: val };
      added = true;
    } else if (Array.isArray(val) && val.length) {
      // Ensure all items in the array are strings and non-empty
      const validItems = val.filter(x => x && typeof x === 'string' && x.trim() !== '');
      if (validItems.length) {
        attrValues[valKey] = { L: validItems.map(x => ({ S: String(x) })) };
        added = true;
      }
    } else if (typeof val === 'string' && val) {
      attrValues[valKey] = { S: val };
      added = true;
    }

    if (added) {
      attrNames[nameKey] = key;
      exprParts.push(`${nameKey} = ${valKey}`);
      counter++;
    }
  });

  // always update the timestamp
  attrNames['#u'] = 'updatedAt';
  exprParts.push('#u = :u');

  await db.send(new UpdateItemCommand({
    TableName: TABLE,
    Key: {
      itemType: { S: itemType },
      itemId:   { S: itemId }
    },
    UpdateExpression:           'SET ' + exprParts.join(', '),
    ExpressionAttributeNames:   attrNames,
    ExpressionAttributeValues:  attrValues
  }));
  
  // After updating the inventory item, check if we need to sync with product listing
  if (updates.quantity !== undefined || updates.packagedCount !== undefined) {
    // Get the full item to have all data needed for sync
    const updatedItem = await getItem(itemType, itemId);
    if (updatedItem && updatedItem.productRefId) {
      await syncWithProductListing(updatedItem);
    }
  }
}

export async function deleteItem(itemType, itemId) {
  const db = await makeClient();
  await db.send(new DeleteItemCommand({
    TableName: TABLE,
    Key: {
      itemType: { S: itemType },
      itemId:   { S: itemId }
    }
  }));
}
